import { useCallback, useEffect, useRef, useState } from 'react';
import { GoogleGenAI, Modality, RealtimeInputConfig, Session, LiveServerMessage } from '@google/genai';
import { AiInterviewSession, TranscriptSpeaker } from '../types';
import { base64ToPcm16, MicStreamHandle, PcmPlayer, pcm16ToBase64 } from '../lib/audio';
import { GENAI_API_VERSION } from '../utils/interviewEnv';
import { useMicrophone } from './useMicrophone';

export type LiveConnectionState = 'idle' | 'connecting' | 'open' | 'closed' | 'error';

export interface LiveSessionEvents {
  onTranscription: (speaker: TranscriptSpeaker, text: string, isFinal: boolean) => void;
  onInterrupted: () => void;
  onTurnComplete: () => void;
  onOpen: () => void;
  onError: (message: string) => void;
  /** Fired on mic-energy transitions — tells the consumer the candidate is still talking. */
  onUserVoice?: (active: boolean) => void;
}

export interface LiveSessionDriver {
  state: LiveConnectionState;
  error: string | null;
  micOn: boolean;
  micError: string | null;
  isAiSpeaking: boolean;
  start: () => Promise<void>;
  stop: () => Promise<void>;
  /** Asks the interviewer to conclude the session (real: synthesized user turn). */
  signalWrapUp: () => void;
}

/** Optional recovery hooks for re-establishing a dropped live session. */
export interface LiveSessionReconnectOptions {
  /** Mints a fresh ephemeral token from the backend (`POST /ai-interviews/{id}/session`). */
  refreshToken?: () => Promise<AiInterviewSession | null>;
  /**
   * Returns a short prompt that restores conversational context after a
   * reconnect, or `null` on a fresh start (which uses the standard intro prompt).
   */
  getResumeContext?: () => string | null;
}

const AUDIO_MIME = 'audio/pcm;rate=16000';
const CONNECT_ERROR_MESSAGE = 'Could not connect to the AI interviewer. Please try again.';
const BEGIN_INTERVIEW_PROMPT = 'Please begin the interview now.';
const WRAP_UP_PROMPT = 'That is all for today. Please thank me and wrap up the session.';
const VOICE_RMS_THRESHOLD = 300;
const MAX_RECONNECT_ATTEMPTS = 2;
const RECONNECT_BACKOFF_MS = 1500;
// How long the upstream mic stays muted AFTER the interviewer's last spoken
// word, so Gemini can never hear its own tail echo through the speakers and
// mistake it for the candidate answering.
const MIC_UNMUTE_GRACE_MS = 900;
const RECONNECT_FAILURE_MESSAGE =
  'The interview connection was lost and could not be restored. You can reconnect or end the interview.';

const delay = (ms: number) => new Promise<void>((resolve) => window.setTimeout(resolve, ms));

/**
 * Drives a real Gemini Live voice session in the browser.
 *
 * Flow: `POST /session` mints a short-lived ephemeral token server-side; this
 * hook hands it to the official `@google/genai` SDK (which opens its own
 * WebSocket, using the SDK's v1alpha ephemeral-token path), streams the
 * microphone as 16 kHz PCM16LE realtime input, and plays the model's 24 kHz
 * audio response back through `PcmPlayer`. The permanent Gemini key never
 * touches the browser.
 *
 * If the WebSocket drops mid-interview (flaky mobile/WiFi, server hiccup),
 * the session auto-reconnects up to `MAX_RECONNECT_ATTEMPTS` times using a
 * freshly-minted token and resumes conversationally (the consumer supplies a
 * resume prompt replaying the transcript). If recovery fails, `events.onError`
 * fires so the UI can leave any stale stage and offer reconnect/end actions —
 * never leaving the interview stranded in "Listening…".
 */
export function useGeminiLiveSession(
  session: AiInterviewSession | null,
  events: LiveSessionEvents,
  options?: LiveSessionReconnectOptions
): LiveSessionDriver {
  const { micOn, micError, startMic, stopMic } = useMicrophone();
  const [state, setState] = useState<LiveConnectionState>('idle');
  const [error, setError] = useState<string | null>(null);
  const [isAiSpeaking, setIsAiSpeaking] = useState(false);

  const sessionRef = useRef(session);
  sessionRef.current = session;
  const eventsRef = useRef(events);
  eventsRef.current = events;
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const clientRef = useRef<GoogleGenAI | null>(null);
  const liveSessionRef = useRef<Session | null>(null);
  const playerRef = useRef<PcmPlayer | null>(null);
  const stoppedRef = useRef(true);
  const startingRef = useRef(false);
  const serverSendingRef = useRef(false);
  const drainTimeoutRef = useRef<number | null>(null);
  const reconnectCountRef = useRef(0);
  const reconnectInflightRef = useRef(false);
  // Set once a session has fully opened (streaming audio) in the current
  // start() cycle; gates auto-reconnect to genuine mid-session drops so a
  // connect-phase callback can never tear down the very connection that is
  // still being established.
  const openOnceRef = useRef(false);
  // While the interviewer is speaking, upstream mic audio is muted so Gemini
  // cannot hear its own output looping back through the speakers (self-echo).
  // Without this the model "hears" the introduction it is still speaking and
  // interrupts itself, producing overlapping/smeared speech.
  const mutedRef = useRef(false);
  const unmuteTimeoutRef = useRef<number | null>(null);
  const signalWrapUpRef = useRef<VoidFunction>(() => {});
  // Gemini's outputAutoTranscription rarely marks `finished`, so a question's
  // text is accumulated here across partials and committed as an AI FINAL line
  // when the model turn completes — guaranteeing every interviewer question is
  // stored as its own labelled turn in the transcript.
  const aiFlushTextRef = useRef('');
  const aiFlushDoneRef = useRef(false);

  const checkPlaybackComplete = useCallback(() => {
    if (serverSendingRef.current) {
      // Gemini server is still transmitting audio chunks for this turn
      return;
    }
    const player = playerRef.current;
    if (player && player.buffered() > 0) {
      // Audio is still actively queued or playing through the speakers
      return;
    }
    // Both server transmission is finished AND the speaker buffer has drained.
    if (drainTimeoutRef.current != null) {
      window.clearTimeout(drainTimeoutRef.current);
    }
    // Acoustic decay hangover window (500 ms) so physical room echo
    // and OS audio buffer latency clear completely before unmuting the mic.
    drainTimeoutRef.current = window.setTimeout(() => {
      drainTimeoutRef.current = null;
      if (stoppedRef.current) return;
      if (serverSendingRef.current) return;
      if (playerRef.current && playerRef.current.buffered() > 0) return;

      setIsAiSpeaking(false);
      eventsRef.current.onTurnComplete();
      // Echo protection: keep the mic journaling mute for a grace window after
      // the last spoken word. The tail syllables of the question can still
      // reach Gemini's ears unassisted — if it "hears" itself and treats that
      // as the candidate's answer, it concludes the interview without ever
      // waiting. Unmuting late swallows that tail. Any new audio starts a new
      // turn and cancels this timer (audio is re-muted there).
      if (unmuteTimeoutRef.current == null) {
        unmuteTimeoutRef.current = window.setTimeout(() => {
          unmuteTimeoutRef.current = null;
          if (!stoppedRef.current) mutedRef.current = false;
        }, MIC_UNMUTE_GRACE_MS);
      }
    }, 500);
  }, []);

  const handleMessage = useCallback((msg: LiveServerMessage) => {
    const sc = msg.serverContent;
    if (!sc) return;

    if (sc.interrupted) {
      if (drainTimeoutRef.current != null) {
        window.clearTimeout(drainTimeoutRef.current);
        drainTimeoutRef.current = null;
      }
      serverSendingRef.current = false;
      playerRef.current?.interrupt();
      eventsRef.current.onInterrupted();
      if (unmuteTimeoutRef.current != null) {
        window.clearTimeout(unmuteTimeoutRef.current);
        unmuteTimeoutRef.current = null;
      }
      mutedRef.current = false;
      setIsAiSpeaking(false);
      aiFlushTextRef.current = '';
      aiFlushDoneRef.current = false;
      return;
    }
    if (sc.turnComplete) {
      serverSendingRef.current = false;
      // Gemini rarely marks outputTranscription `finished`, so commit the
      // accumulated question text as a final AI line here — this is what makes
      // every interviewer question a labelled turn in the transcript.
      if (!aiFlushDoneRef.current && aiFlushTextRef.current.trim()) {
        eventsRef.current.onTranscription('AI', aiFlushTextRef.current.trim(), true);
      }
      aiFlushTextRef.current = '';
      aiFlushDoneRef.current = false;
      // Do NOT unmute or fire onTurnComplete yet.
      // Gemini has only finished SENDING the audio over the WebSocket.
      // PcmPlayer is still actively playing the queued audio through the speakers.
      // We must wait until PcmPlayer actually drains its audio buffer.
      checkPlaybackComplete();
    }

    const input = sc.inputTranscription;
    if (input && input.text) {
      eventsRef.current.onTranscription('CANDIDATE', input.text, Boolean(input.finished));
    }
    const output = sc.outputTranscription;
    if (output && output.text) {
      eventsRef.current.onTranscription('AI', output.text, Boolean(output.finished));
      const acc = aiFlushTextRef.current;
      aiFlushTextRef.current =
        acc && output.text.startsWith(acc) ? output.text : acc + output.text;
      if (output.finished) aiFlushDoneRef.current = true;
    }

    const audio = msg.data;
    if (audio) {
      serverSendingRef.current = true;
      if (unmuteTimeoutRef.current != null) {
        window.clearTimeout(unmuteTimeoutRef.current);
        unmuteTimeoutRef.current = null;
      }
      mutedRef.current = true;
      setIsAiSpeaking(true);
      if (drainTimeoutRef.current != null) {
        window.clearTimeout(drainTimeoutRef.current);
        drainTimeoutRef.current = null;
      }
      playerRef.current?.pushPcm16(base64ToPcm16(audio));
    }
    if ((sc.modelTurn?.parts?.length ?? 0) > 0) {
      serverSendingRef.current = true;
      if (unmuteTimeoutRef.current != null) {
        window.clearTimeout(unmuteTimeoutRef.current);
        unmuteTimeoutRef.current = null;
      }
      mutedRef.current = true;
      setIsAiSpeaking(true);
      if (drainTimeoutRef.current != null) {
        window.clearTimeout(drainTimeoutRef.current);
        drainTimeoutRef.current = null;
      }
    }
  }, [checkPlaybackComplete]);

  const teardownResources = useCallback(() => {
    if (drainTimeoutRef.current != null) {
      window.clearTimeout(drainTimeoutRef.current);
      drainTimeoutRef.current = null;
    }
    if (unmuteTimeoutRef.current != null) {
      window.clearTimeout(unmuteTimeoutRef.current);
      unmuteTimeoutRef.current = null;
    }
    serverSendingRef.current = false;
    startingRef.current = false;
    liveSessionRef.current = null;
    clientRef.current = null;
    signalWrapUpRef.current = () => {};
    mutedRef.current = false;
    setIsAiSpeaking(false);
    playerRef.current?.stop();
    playerRef.current = null;
    stopMic();
    aiFlushTextRef.current = '';
    aiFlushDoneRef.current = false;
  }, [stopMic]);

  const pumpMicAudio = useCallback((mic: MicStreamHandle, conn: Session) => {
    let voiceActive = false;
    let lastVoiceMs = 0;
    let sumSq = 0;
    let windowSamples = 0;
    void (async () => {
      try {
        for await (const chunk of mic.chunks) {
          if (stoppedRef.current) break;
          if (!mutedRef.current) {
            conn.sendRealtimeInput({
              audio: {
                data: pcm16ToBase64(new Uint8Array(chunk)),
                mimeType: AUDIO_MIME,
              },
            });
          }

          const pcm = new Int16Array(chunk.buffer, chunk.byteOffset, chunk.byteLength >> 1);
          for (let i = 0; i < pcm.length; i++) {
            const v = pcm[i];
            sumSq += v * v;
            windowSamples++;
          }
          if (windowSamples >= 1600) {
            const rms = Math.sqrt(sumSq / windowSamples);
            sumSq = 0;
            windowSamples = 0;
            const audible = rms > VOICE_RMS_THRESHOLD;
            const now = Date.now();
            if (audible) lastVoiceMs = now;
            const isActive = audible || now - lastVoiceMs < 650;
            if (isActive !== voiceActive) {
              voiceActive = isActive;
              if (!stoppedRef.current) {
                eventsRef.current.onUserVoice?.(isActive);
              }
            }
          }
        }
      } catch {
        // The stream was closed — nothing to do.
      }
    })();
  }, []);

  const reconnectRef = useRef<(message: string) => void>(() => {});
  const establish = useCallback(async () => {
    const current = sessionRef.current;
    if (!current) return;
    if (startingRef.current || liveSessionRef.current) return;

    startingRef.current = true;
    setError(null);
    setState('connecting');

    // Mute mic immediately so ambient noise while the AI prepares its intro
    // does not bleed into the model.
    mutedRef.current = true;
    serverSendingRef.current = true;
    setIsAiSpeaking(true);

    if (drainTimeoutRef.current != null) {
      window.clearTimeout(drainTimeoutRef.current);
      drainTimeoutRef.current = null;
    }

    if (playerRef.current) {
      playerRef.current.stop();
      playerRef.current = null;
    }

    const player = new PcmPlayer();
    player.ensureContext();
    player.onDrain = () => {
      checkPlaybackComplete();
    };
    playerRef.current = player;

    const ai = new GoogleGenAI({
      apiKey: current.token,
      httpOptions: { apiVersion: GENAI_API_VERSION },
    });
    clientRef.current = ai;

    let conn: Session;
    try {
      conn = await ai.live.connect({
        model: current.model,
        config: {
          responseModalities: ['AUDIO' as Modality],
          speechConfig: {
            voiceConfig: { prebuiltVoiceConfig: { voiceName: current.voice } },
          },
          systemInstruction: { parts: [{ text: current.systemInstruction }] },
          // Note: inputAudioTranscription is intentionally OFF — Gemini's live
          // input transcription rarely emits finals, and when it does it would
          // double-count against the reliable local SpeechRecognition finetext.
          outputAudioTranscription: {},
          realtimeInputConfig: { automaticActivityDetection: {} } as unknown as RealtimeInputConfig,
        },
        callbacks: {
          onopen: () => {},
          onmessage: handleMessage,
          onerror: () => {
            if (stoppedRef.current) return;
            reconnectRef.current(RECONNECT_FAILURE_MESSAGE);
          },
          onclose: () => {
            if (stoppedRef.current) return;
            reconnectRef.current(RECONNECT_FAILURE_MESSAGE);
          },
        },
      });
    } catch (err) {
      if (stoppedRef.current) return;
      teardownResources();
      throw err instanceof Error && err.message ? err : new Error(CONNECT_ERROR_MESSAGE);
    }

    if (stoppedRef.current) {
      conn.close();
      return;
    }

    liveSessionRef.current = conn;
    signalWrapUpRef.current = () => {
      try {
        conn.sendClientContent({
          turns: [{ role: 'user', parts: [{ text: WRAP_UP_PROMPT }] }],
          turnComplete: true,
        });
      } catch {
        // connection already gone — the finish failsafe will take over
      }
    };

    let mic: MicStreamHandle;
    try {
      mic = await startMic();
    } catch (err) {
      teardownResources();
      throw new Error(CONNECT_ERROR_MESSAGE);
    }
    if (stoppedRef.current) {
      mic.stop();
      conn.close();
      return;
    }

    setState('open');
    openOnceRef.current = true;
    eventsRef.current.onOpen();
    const resumePrompt = optionsRef.current?.getResumeContext?.() ?? null;
    conn.sendClientContent({
      turns: [{ role: 'user', parts: [{ text: resumePrompt ?? BEGIN_INTERVIEW_PROMPT }] }],
      turnComplete: true,
    });
    pumpMicAudio(mic, conn);
  }, [checkPlaybackComplete, handleMessage, pumpMicAudio, startMic, teardownResources]);

  reconnectRef.current = (message: string) => {
    if (stoppedRef.current) return;
    if (!openOnceRef.current) return;
    if (reconnectInflightRef.current) return;
    reconnectInflightRef.current = true;
    void (async () => {
      try {
        for (let attempt = 1; attempt <= MAX_RECONNECT_ATTEMPTS; attempt += 1) {
          if (attempt > 1) await delay(RECONNECT_BACKOFF_MS * (attempt - 1));
          if (stoppedRef.current) return;
          reconnectCountRef.current = attempt;
          setState('connecting');
          teardownResources();
          try {
            const refreshed = optionsRef.current?.refreshToken
              ? await optionsRef.current.refreshToken()
              : null;
            if (refreshed) sessionRef.current = refreshed;
            if (stoppedRef.current) return;
            await establish();
          } catch {
            // Try again (or give up below).
          }
          if (stoppedRef.current) return;
          if (liveSessionRef.current) {
            reconnectCountRef.current = 0;
            return;
          }
        }
        if (!stoppedRef.current) {
          setError(message);
          setState('error');
          eventsRef.current.onError(message);
        }
      } finally {
        reconnectCountRef.current = 0;
        reconnectInflightRef.current = false;
      }
    })();
  };

  const start = useCallback(async () => {
    if (startingRef.current) return;
    stoppedRef.current = false;
    reconnectCountRef.current = 0;
    reconnectInflightRef.current = false;
    openOnceRef.current = false;
    try {
      await establish();
    } catch (err) {
      if (stoppedRef.current) return;
      const message =
        err instanceof Error && err.message ? err.message : CONNECT_ERROR_MESSAGE;
      setError(message);
      setState('error');
      eventsRef.current.onError(message);
    }
  }, [establish]);

  const stop = useCallback(async () => {
    stoppedRef.current = true;
    startingRef.current = false;
    reconnectInflightRef.current = false;
    reconnectCountRef.current = 0;
    openOnceRef.current = false;
    if (drainTimeoutRef.current != null) {
      window.clearTimeout(drainTimeoutRef.current);
      drainTimeoutRef.current = null;
    }
    setIsAiSpeaking(false);
    liveSessionRef.current?.close();
    teardownResources();
    setState('closed');
    setError(null);
  }, [teardownResources]);

  useEffect(() => () => {
    stoppedRef.current = true;
    reconnectInflightRef.current = false;
    if (drainTimeoutRef.current != null) {
      window.clearTimeout(drainTimeoutRef.current);
      drainTimeoutRef.current = null;
    }
    liveSessionRef.current?.close();
    signalWrapUpRef.current = () => {};
    setIsAiSpeaking(false);
    playerRef.current?.stop();
    stopMic();
  }, [stopMic]);

  return {
    state,
    error,
    micOn,
    micError,
    isAiSpeaking,
    start,
    stop,
    signalWrapUp: () => signalWrapUpRef.current(),
  };
}